import {
	createFileRoute,
	Outlet,
	useLocation,
	useNavigate,
} from "@tanstack/react-router";
import { useEffect } from "react";
import { DashboardProvider } from "@/components/dashboard/DashboardContext";
import {
	DashboardBottomNav,
	DashboardMobileTopBar,
	DashboardSidebar,
} from "@/components/dashboard/DashboardSidebar";
import { ImpersonationBanner } from "@/components/header/ImpersonationBanner";
import { LoadingScreen } from "@/components/LoadingScreen";
import { OnboardingChecklist } from "@/components/onboarding/OnboardingChecklist";
import { WelcomeDialog } from "@/components/onboarding/WelcomeDialog";
import { useSession } from "@/hooks/auth-hooks";
import { useSubscription } from "@/hooks/use-subscription";
import { noIndexMeta } from "@/lib/seo";

export const Route = createFileRoute("/dashboard")({
	component: DashboardLayoutRoute,
	head: () => ({
		meta: noIndexMeta(),
	}),
});

function DashboardLayoutRoute() {
	const navigate = useNavigate();
	const routeLocation = useLocation();
	const { session, user, isPending } = useSession();
	const { isLoading: isSubscriptionLoading } = useSubscription();

	useEffect(() => {
		if (isPending) return;
		if (!user) {
			navigate({ to: "/signup", replace: true });
		}
	}, [user, isPending, navigate]);

	useEffect(() => {
		function handleKeyDown(event: KeyboardEvent) {
			if (event.key !== "c" && event.key !== "C") return;
			if (event.metaKey || event.ctrlKey || event.altKey) return;
			const target = event.target as HTMLElement | null;
			if (!target) return;
			if (target.isContentEditable) return;
			const tag = target.tagName;
			if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
			if (routeLocation.pathname === "/dashboard/compose") return;
			event.preventDefault();
			navigate({ to: "/dashboard/compose" });
		}
		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [navigate, routeLocation.pathname]);

	if (isPending || !user || isSubscriptionLoading) {
		return <LoadingScreen />;
	}

	const isImpersonating = !!session?.impersonatedBy;

	return (
		<DashboardProvider>
			<div data-dashboard-root className="flex h-svh bg-background">
				<DashboardSidebar />
				<div className="flex min-w-0 flex-1 flex-col pb-[calc(env(safe-area-inset-bottom)+3.75rem)] sm:pb-0">
					<ImpersonationBanner isImpersonating={isImpersonating} user={user} />
					<DashboardMobileTopBar />
					<Outlet />
				</div>
				<DashboardBottomNav />
				<WelcomeDialog />
				<OnboardingChecklist />
			</div>
		</DashboardProvider>
	);
}
